import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import WarpField from "@/components/WarpField";

/**
 * Cinematic intro: aerospace boot → gun-barrel target → warp jump → JITSUENT title → cut.
 */
export default function WarpIntro() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState(0);
  // 0: boot/checks, 1: gun-barrel target, 2: warp jump, 3: title reveal, 4: cut-white

  useEffect(() => {
    const seq = [
      [0, 1, 1400],
      [1, 2, 1800],
      [2, 3, 1800],
      [3, 4, 1200],
    ];
    const timers = seq.map(([_from, to, delay]) =>
      setTimeout(() => setPhase(to), seq.slice(0, seq.findIndex((s) => s[1] === to) + 1).reduce((a, s) => a + s[2], 0))
    );
    const final = setTimeout(() => navigate("/"), 6800);
    return () => { timers.forEach(clearTimeout); clearTimeout(final); };
  }, [navigate]);

  return (
    <div className="fixed inset-0 bg-black overflow-hidden grain-overlay scanlines" data-testid="warp-intro">
      {/* Phase 0 — boot checks */}
      <AnimatePresence>
        {phase === 0 && (
          <motion.div
            key="boot"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex flex-col items-center justify-center font-mono text-[10px] tracking-[0.4em] text-neutral-400 uppercase"
          >
            <div className="absolute top-12 left-12 right-12 flex justify-between text-neutral-600 text-[9px]">
              <span>JITSUENT // MEDIA-OPS</span>
              <span className="cursor-blink">BOOT SEQUENCE</span>
            </div>
            <div className="space-y-2 text-left w-[280px]">
              {[
                "SYSTEM CHECK ........ OK",
                "OPTICS CALIBRATION .. OK",
                "WARP CORE ........... ENGAGED",
                "SIGNAL LOCK ......... STABLE",
                "ROLL .................. READY",
              ].map((line, i) => (
                <motion.div
                  key={line}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.18 }}
                  className="text-neutral-500"
                >
                  <span className="text-white">›</span> {line}
                </motion.div>
              ))}
            </div>
            <div className="absolute bottom-12 left-12 right-12 flex justify-between text-neutral-700 text-[9px]">
              <span>CALLSIGN: BONSAI-01</span>
              <span>CLEARANCE: TS-FAMILY</span>
            </div>
          </motion.div>
        )}

        {/* Phase 1 — Gun-barrel target / aerospace reticle */}
        {phase === 1 && (
          <motion.div
            key="target"
            initial={{ opacity: 0, scale: 1.3 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div className="relative" style={{ width: 480, height: 480 }}>
              {/* Concentric circles */}
              {[1, 0.78, 0.56, 0.34, 0.18].map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: i * 0.12 }}
                  className="absolute inset-0 m-auto border border-white/50 rounded-full"
                  style={{ width: `${s * 100}%`, height: `${s * 100}%` }}
                />
              ))}
              {/* Crosshair */}
              <div className="absolute top-1/2 left-0 right-0 h-px bg-white/70" />
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/70" />
              {/* Center dot */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full flicker" />
              {/* Corner readouts */}
              <div className="absolute -top-8 left-0 font-mono text-[9px] tracking-[0.3em] text-neutral-500">LAT 34.0522° N</div>
              <div className="absolute -top-8 right-0 font-mono text-[9px] tracking-[0.3em] text-neutral-500 text-right">LNG 118.2437° W</div>
              <div className="absolute -bottom-8 left-0 font-mono text-[9px] tracking-[0.3em] text-neutral-500">TARGET LOCK</div>
              <div className="absolute -bottom-8 right-0 font-mono text-[9px] tracking-[0.3em] text-white text-right cursor-blink">ENGAGE</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Phase 2 — Warp jump (overlay stays through phase 3 too) */}
      <AnimatePresence>
        {phase >= 2 && phase < 4 && (
          <motion.div
            key="warp"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0"
          >
            <WarpField density={340} speed={phase === 2 ? 6 : 3} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Phase 3 — Title reveal */}
      <AnimatePresence>
        {phase === 3 && (
          <motion.div
            key="title"
            initial={{ opacity: 0, scale: 1.4 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div className="text-center">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="font-mono text-[10px] tracking-[0.6em] text-neutral-500 mb-6"
              >
                PRESENTED BY THE FAMILY
              </motion.div>
              <div className="font-bodoni text-7xl md:text-9xl text-white tracking-tight">
                JITSUENT
              </div>
              <div className="font-mono text-[10px] tracking-[0.6em] text-neutral-500 mt-6">
                MEDIA &nbsp; & &nbsp; PRODUCTION
              </div>
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="h-px chrome-border w-64 mx-auto mt-6 origin-center"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Phase 4 — film cut to white */}
      {phase === 4 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.08 }}
          className="absolute inset-0 bg-white"
        />
      )}
    </div>
  );
}
